import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useDevice } from "@/lib/useDevice";

/**
 * SidebarAdList — fetches ALL active creatives for a slot and stacks them.
 * Renders nothing when there are no creatives (no placeholders).
 */
export default function SidebarAdList({ slot, category, articleId }) {
  const device = useDevice();
  const [creatives, setCreatives] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const seenRef = useRef(new Set());

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    api.serveAdAll(slot, device, category, articleId)
      .then((r) => {
        if (!alive) return;
        setCreatives(r?.creatives || []);
        setLoaded(true);
      })
      .catch(() => {
        if (alive) { setCreatives([]); setLoaded(true); }
      });
    return () => { alive = false; };
  }, [slot, device, category, articleId]);

  // Log impressions once per creative
  useEffect(() => {
    creatives.forEach((c) => {
      if (!seenRef.current.has(c.id)) {
        seenRef.current.add(c.id);
        api.logAdEvent(c.id, "impression", device);
      }
    });
  }, [creatives, device]);

  if (!loaded || creatives.length === 0) return null;

  return (
    <>
      {creatives.map((c) => {
        const clickable = !!c.target_url && c.creative_type !== "html" && c.creative_type !== "embed";
        const handleClick = () => api.logAdEvent(c.id, "click", device);
        const inner = c.creative_type === "html" || c.creative_type === "embed" ? (
          <div className="w-full max-w-full overflow-hidden" dangerouslySetInnerHTML={{ __html: c.html_code || "" }} />
        ) : c.creative_type === "video" ? (
          <video src={c.asset_url} className="w-full h-auto max-w-full block" autoPlay muted loop playsInline />
        ) : (
          <img src={c.asset_url} alt={c.alt || ""} loading="lazy" className="block w-full h-auto max-w-full" />
        );
        return (
          <div key={c.id} data-ad-slot={slot} data-ad-creative={c.id} className="ad-slot">
            {clickable ? (
              <a href={c.target_url} onClick={handleClick} target="_blank" rel="noopener noreferrer sponsored" className="block">
                {inner}
              </a>
            ) : (
              <div onClick={c.target_url ? handleClick : undefined}>{inner}</div>
            )}
          </div>
        );
      })}
    </>
  );
}
